/**
 * 掲示板投稿 (board_posts) の hooks。
 * 仕様書: docs/02_work/done/plan_board_feature.md §2-2, §5-1
 *
 * - 一覧・詳細読込は Firestore 直接 (read は rules の isInternal で保護)
 * - 書込は Cloud Function Callable 経由のみ (upsertBoardPost / deleteBoardPost)
 * - postId は新規作成時にクライアントで事前採番し upsert に渡す
 *   (Phase 3 で本文メディアのアップロード先 board/imgs/{postId}/ を確定させるための契約)
 */
import type { BoardPost, BoardPostType, BoardRecipientState, BoardUrgency } from '@goenhub/shared';
import { BOARD_COLLECTIONS } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';
import { toDate } from '@/lib/firestoreTime';

export interface BoardPostRecord extends BoardPost {
  id: string;
}

// ─────────────────────────────────────────────
// Query
// ─────────────────────────────────────────────

/**
 * 一覧の並び順キー（新しい順）。
 *
 * 投稿予約に対応するため createdAt そのままではなく:
 *   公開済み → publishedAt（無ければ createdAt。予約機能より前の既存投稿）
 *   予約中   → publishAt（未来 = 先頭に来る。投稿者本人にしか見えない）
 * とする。予約投稿が「作成した時点の位置」に埋もれるのを防ぐ。
 */
function sortKeyOf(post: BoardPost): number {
  const d = toDate(post.publishedAt) ?? toDate(post.publishAt) ?? toDate(post.createdAt);
  return d ? d.getTime() : 0;
}

export function useBoardPosts() {
  return useQuery({
    queryKey: ['board_posts'],
    queryFn: async (): Promise<BoardPostRecord[]> => {
      // orderBy は createdAt のまま（publishedAt は既存 doc に無く、
      // orderBy に使うとフィールド欠損 doc が丸ごとクエリから落ちるため）。
      // 予約を考慮した並びは取得後にクライアントで整える。
      const q = query(collection(db, BOARD_COLLECTIONS.posts), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as BoardPost) }))
        .sort((a, b) => sortKeyOf(b) - sortKeyOf(a));
    },
  });
}

export function useBoardPost(id: string | null | undefined) {
  return useQuery({
    queryKey: ['board_posts', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<BoardPostRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, BOARD_COLLECTIONS.posts, id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as BoardPost) };
    },
  });
}

/** 新規投稿の postId をクライアントで事前採番する（plan §1-6 の契約）。 */
export function newBoardPostId(): string {
  return doc(collection(db, BOARD_COLLECTIONS.posts)).id;
}

// ─────────────────────────────────────────────
// Mutations (Callable)
// ─────────────────────────────────────────────

/**
 * upsertBoardPost の入力。
 * bodyText はサーバ側で bodyHtml から生成される。
 * audience はサーバ側導出: recipientUids 非空 → 'selected' / 空・未指定 → 'all'。
 */
export interface UpsertBoardPostInput {
  /** 新規=事前採番 / 編集=既存 doc id。 */
  postId: string;
  type: BoardPostType;
  title: string;
  bodyHtml: string;
  categoryId: string | null;
  urgency: BoardUrgency;
  /** 'YYYY-MM-DD'。null = 無期日。 */
  dueDate: string | null;
  /**
   * 宛先 uid (最大50名)。空 or 未指定 = 全員宛。
   * サーバ側で dedupe・投稿者本人の除外・内部 active メンバー検証が行われ、
   * 編集時は recipientStates が差分マージされる (plan §2-2-3)。
   */
  recipientUids?: string[];
  /**
   * 投稿予約: 公開日時の ISO 文字列。null = 即時公開。
   * 1分未満の未来・過去はサーバ側で即時公開に丸められる。
   * 既に公開済みの投稿では無視される（予約に戻すことはできない）。
   */
  publishAt?: string | null;
  /**
   * 閲覧制限: true = 宛先と投稿者本人だけが一覧・詳細で閲覧できる。
   * 未指定 = false。宛先が全員のときは効果を持たない。
   */
  restricted?: boolean;
}

// サーバ (functions/src/board/posts.ts) の実際の返却値に型を合わせる
const upsertBoardPostFn = httpsCallable<UpsertBoardPostInput, { postId: string }>(
  functions,
  'upsertBoardPost',
);

const deleteBoardPostFn = httpsCallable<{ postId: string }, { postId: string }>(
  functions,
  'deleteBoardPost',
);

// Phase 2: 宛先ステータス系 Callable (plan §5-1)
const markBoardPostViewedFn = httpsCallable<{ postId: string }, { updated: boolean }>(
  functions,
  'markBoardPostViewed',
);

const actOnBoardPostFn = httpsCallable<
  {
    postId: string;
    state: BoardRecipientState;
    report?: { url?: string; bodyHtml?: string };
  },
  { postId: string; state: BoardRecipientState }
>(functions, 'actOnBoardPost');

const closeBoardPostFn = httpsCallable<{ postId: string }, { postId: string }>(
  functions,
  'closeBoardPost',
);

const reopenBoardPostFn = httpsCallable<{ postId: string }, { postId: string }>(
  functions,
  'reopenBoardPost',
);

const publishBoardPostNowFn = httpsCallable<{ postId: string }, { postId: string }>(
  functions,
  'publishBoardPostNow',
);

export function useUpsertBoardPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertBoardPostInput) => {
      const res = await upsertBoardPostFn(input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_posts'] }),
  });
}

export function useDeleteBoardPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const res = await deleteBoardPostFn({ postId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_posts'] }),
  });
}

/**
 * 予約中の投稿を予約時刻を待たずに公開する（投稿者本人のみ）。
 * 公開と同時に通知（新規投稿）が送信される。
 */
export function usePublishBoardPostNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const res = await publishBoardPostNowFn({ postId });
      return res.data;
    },
    onSuccess: (_data, postId) => {
      void qc.invalidateQueries({ queryKey: ['board_posts'] });
      void qc.invalidateQueries({ queryKey: ['board_posts', 'detail', postId] });
    },
  });
}

/**
 * 詳細画面表示時の冪等な既読化/投稿者バッジ消灯 (plan §5-1)。
 * 宛先本人: unread→read / 投稿者本人: authorUnseenActivity=false。
 * 必ず useEffect から呼ぶ (onSnapshot コールバック内での呼び出し禁止 — 家訓)。
 */
export function useMarkBoardPostViewed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const res = await markBoardPostViewedFn({ postId });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['board_posts'] }),
  });
}

/** 宛先本人のアクション (決定/完了/確認済 等) の実行。 */
export function useActOnBoardPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      state: BoardRecipientState;
      report?: { url?: string; bodyHtml?: string };
    }) => {
      const res = await actOnBoardPostFn(input);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      // prefix 一致で一覧・詳細の両方が無効化されるが、詳細 key は明示的にも無効化しておく
      void qc.invalidateQueries({ queryKey: ['board_posts'] });
      void qc.invalidateQueries({ queryKey: ['board_posts', 'detail', vars.postId] });
      // 対応内容の表示も更新する
      void qc.invalidateQueries({ queryKey: ['board_action_reports', vars.postId] });
    },
  });
}

// ─────────────────────────────────────────────
// Phase 4: AI 件名アシスト (plan §4 / §7-1)
// ─────────────────────────────────────────────

/**
 * assistBoardSubject Callable の入出力。
 * - 件名 (title) 入力済み → その件名をブラッシュアップした候補を返す
 * - 件名 未入力 → 本文 (bodyText) から件名候補を生成する
 * bodyText はクライアント側で bodyHtml をプレーンテキスト化して渡す。
 */
const assistBoardSubjectFn = httpsCallable<
  { title?: string; bodyText: string },
  { suggestions: string[] }
>(functions, 'assistBoardSubject');

/** AI に件名候補を生成させる (軽量モデル / CLAUDE_MODELS.haiku 想定)。 */
export function useAssistBoardSubject() {
  return useMutation({
    mutationFn: async (input: { title?: string; bodyText: string }) => {
      const res = await assistBoardSubjectFn(input);
      return res.data.suggestions;
    },
  });
}

/** 投稿のクローズ (投稿者 or 宛先者)。openRecipientUids は不変条件により空になる。 */
export function useCloseBoardPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const res = await closeBoardPostFn({ postId });
      return res.data;
    },
    onSuccess: (_data, postId) => {
      void qc.invalidateQueries({ queryKey: ['board_posts'] });
      void qc.invalidateQueries({ queryKey: ['board_posts', 'detail', postId] });
    },
  });
}

/**
 * クローズした投稿の再オープン (**投稿者本人のみ**。2026-08-06 追加)。
 * 未対応の宛先がバッジ・期日リマインダーへ戻り、宛先へ再オープン通知が飛ぶ
 * (通知は onBoardPostUpdatedNotify トリガー側)。
 */
export function useReopenBoardPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const res = await reopenBoardPostFn({ postId });
      return res.data;
    },
    onSuccess: (_data, postId) => {
      void qc.invalidateQueries({ queryKey: ['board_posts'] });
      void qc.invalidateQueries({ queryKey: ['board_posts', 'detail', postId] });
    },
  });
}
